import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Box, Button, ButtonGroup, CircularProgress, Stack, Typography } from "@mui/material";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useAutoScroll } from "../hooks/use-auto-scroll";
import type { TestRunStep } from "../types";
import { buildStepTreeMetadata, countChildren, getVisibleSteps } from "../utils/step-tree-utils";
import { TestRunStepCard } from "./test-run-step-card";

interface TestRunStepsListProps {
  steps: TestRunStep[] | undefined;
  isLoading: boolean;
  onStepClick?: (step: TestRunStep) => void;
  recordingPath?: string;
}

export const TestRunStepsList: React.FC<TestRunStepsListProps> = ({
  steps,
  isLoading,
  onStepClick,
  recordingPath,
}) => {
  const stackRef = useRef<HTMLDivElement>(null);
  useAutoScroll(stackRef.current, steps?.length);

  // Build tree metadata first (needed for initial collapsed state)
  const metadataMap = useMemo(() => {
    if (!steps) return new Map();
    return buildStepTreeMetadata(steps);
  }, [steps]);

  // Initialize with all parent paths collapsed
  const initialCollapsedPaths = useMemo(() => {
    if (!steps) return new Set<string>();
    const allParentPaths = new Set<string>();
    for (const step of steps) {
      const metadata = metadataMap.get(step.id);
      if (metadata?.isParent) {
        allParentPaths.add(metadata.pathKey);
      }
    }
    return allParentPaths;
  }, [steps, metadataMap]);

  // Collapse state: Set of collapsed path keys (start with all collapsed)
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  // Initialize collapsed state when steps are first loaded
  React.useEffect(() => {
    if (steps && steps.length > 0 && !initialized) {
      setCollapsedPaths(initialCollapsedPaths);
      setInitialized(true);
    }
  }, [steps, initialCollapsedPaths, initialized]);

  const handleToggleCollapse = useCallback((pathKey: string) => {
    setCollapsedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(pathKey)) {
        next.delete(pathKey);
      } else {
        next.add(pathKey);
      }
      return next;
    });
  }, []);

  // Collapse All: add all parent paths to collapsed set
  const handleCollapseAll = useCallback(() => {
    if (!steps) return;
    const allParentPaths = new Set<string>();
    for (const step of steps) {
      const metadata = metadataMap.get(step.id);
      if (metadata?.isParent) {
        allParentPaths.add(metadata.pathKey);
      }
    }
    setCollapsedPaths(allParentPaths);
  }, [steps, metadataMap]);

  // Expand All: clear collapsed set
  const handleExpandAll = useCallback(() => {
    setCollapsedPaths(new Set());
  }, []);

  // Filter to visible steps
  const visibleSteps = useMemo(() => {
    if (!steps) return [];
    return getVisibleSteps(steps, metadataMap, collapsedPaths);
  }, [steps, metadataMap, collapsedPaths]);

  // Pre-compute child counts for parent steps
  const childCounts = useMemo(() => {
    if (!steps) return new Map<string, number>();
    const counts = new Map<string, number>();
    for (const step of steps) {
      const metadata = metadataMap.get(step.id);
      if (metadata?.isParent) {
        counts.set(metadata.pathKey, countChildren(metadata.pathKey, steps));
      }
    }
    return counts;
  }, [steps, metadataMap]);

  // Check if there are any parent steps (to show collapse/expand buttons)
  const hasParentSteps = useMemo(() => {
    for (const [, metadata] of metadataMap) {
      if (metadata.isParent) return true;
    }
    return false;
  }, [metadataMap]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <CircularProgress />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
          Loading steps...
        </Typography>
      </Box>
    );
  }

  if (!steps || steps.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No steps recorded for this run.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        overflowY: "auto",
        height: "calc(100vh - 124px)",
        p: 2,
      }}
      ref={stackRef}
    >
      {/* Collapse All / Expand All buttons */}
      {hasParentSteps && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <ButtonGroup size="small" variant="text">
            <Button
              startIcon={<UnfoldLessIcon sx={{ fontSize: 16 }} />}
              onClick={handleCollapseAll}
              sx={{ fontSize: "0.75rem", py: 0.25, px: 1 }}
            >
              Collapse
            </Button>
            <Button
              startIcon={<UnfoldMoreIcon sx={{ fontSize: 16 }} />}
              onClick={handleExpandAll}
              sx={{ fontSize: "0.75rem", py: 0.25, px: 1 }}
            >
              Expand
            </Button>
          </ButtonGroup>
        </Stack>
      )}
      <Stack direction="column" spacing={1} alignItems="stretch">
        {visibleSteps.map((step) => {
          const metadata = metadataMap.get(step.id);
          const pathKey = metadata?.pathKey ?? step.name;
          const isCollapsed = collapsedPaths.has(pathKey);
          const childCount = childCounts.get(pathKey) ?? 0;

          return (
            <TestRunStepCard
              key={step.id + (step.code || "") + (step.updated_at || "")}
              step={step}
              onClick={onStepClick ? () => onStepClick(step) : undefined}
              isClickable={!!recordingPath && !!onStepClick}
              treeMetadata={metadata}
              isCollapsed={isCollapsed}
              onToggleCollapse={handleToggleCollapse}
              childCount={childCount}
            />
          );
        })}
      </Stack>
    </Box>
  );
};
